import { createContext, ReactNode, useEffect, useReducer } from 'react';
// utils
import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';
// @types
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../@types/auth';

// ----------------------------------------------------------------------

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Login]: {
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Register]: undefined;
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
  case 'INITIALIZE':
    return {
      isAuthenticated: action.payload.isAuthenticated,
      isInitialized: true,
      user: action.payload.user,
    };
  case 'LOGIN':
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  case 'LOGOUT':
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };

  case 'REGISTER':
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };

  default:
    return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        const isLocalStorage = localStorage.getItem('accessToken') !== null;

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken, isLocalStorage);

          const response = await axios.get('/api/users/profile');
          const user = response.data;

          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email: string, password: string, remembmer: boolean) => {
    try {
      const authResponse = await axios.post('/api/auth', {
        username: email,
        password,
      });
      const { token } = authResponse.data;

      setSession(token, remembmer);

      const profileResponse = await axios.get('/api/users/profile');
      const user = profileResponse.data;

      dispatch({
        type: Types.Login,
        payload: {
          user,
        },
      });
    } catch (err) {
      throw err;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string, phone: string,
    country: string, institution: string, profession: string) => {
    const response = await axios.post('/api/users', {
      username: email,
      password,
      firstName,
      lastName,
      phone,
      country,
      institution,
      profession
    });
    // const { accessToken, user } = response.data;

    // localStorage.setItem('accessToken', accessToken);

    dispatch({ type: Types.Register });
  };

  const logout = async () => {
    setSession(null, false);
    dispatch({ type: Types.Logout });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
